// JobClose AI · Slice 2T · Levi Intake Coach
// -----------------------------------------------
// Contractor-facing companion for the Full Project intake wizard. Uses
// the same fact function as the Project Workspace snapshot but with a
// visually and tonally distinct presentation. NEVER blocks Next, NEVER
// reroutes the contractor out of intake, NEVER duplicates the wizard's
// step titles.

import { MessageCircleQuestion } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useLeviDrawer } from "@/components/LeviLauncher";
import {
  deriveLeviFacts,
  composeIntakeStatusSentence,
} from "@/lib/leviIntel";

/**
 * Build a small intake-facts view from the wizard's local form state.
 * Only surfaces facts the wizard actually knows — never fabricates
 * counts for things (photos on disk, voice notes) that we can't see
 * from here.
 */
const _formToProjectSlice = (form) => ({
  customer_name: form?.customer_name || "",
  customer_email: form?.customer_email || "",
  customer_phone: form?.customer_phone || "",
  job_address: form?.job_address || "",
  job_type: form?.job_type || "",
  job_description: form?.job_description || "",
  typed_notes: form?.typed_notes || "",
  voice_transcript: form?.voice_transcript || "",
  measurements: form?.measurements || "",
  material_notes: form?.material_notes || "",
  labor_notes: form?.labor_notes || "",
  special_conditions: form?.special_conditions || "",
  customer_concerns: form?.customer_concerns || "",
  status: "draft",
});

const _extraObservations = (facts, t) => {
  const out = [];
  if (facts.customer_name_present === true && facts.job_address_present === false) {
    out.push({ id: "no_job_address", copy_key: "levi_intake_coach.also.no_job_address" });
  }
  if (facts.customer_email_present === true && facts.customer_phone_present === false) {
    out.push({ id: "no_phone", copy_key: "levi_intake_coach.also.no_phone" });
  }
  if (facts.voice_transcript_present === true && facts.typed_notes_present === false) {
    out.push({ id: "have_voice_no_typed", copy_key: "levi_intake_coach.also.have_voice_no_typed" });
  }
  if (facts.job_description_present === true && facts.customer_email_present === false && facts.customer_phone_present === false) {
    out.push({ id: "no_contact_yet", copy_key: "levi_intake_coach.also.no_contact_yet" });
  }
  return out.slice(0, 3).map((o) => ({ ...o, text: t(o.copy_key) }));
};

/**
 * Props:
 *   - form            The wizard form state (from NewEstimate.jsx)
 *   - draftProjectId  Truthy once the draft project exists on the server
 */
export default function LeviIntakeCoach({ form, draftProjectId }) {
  const { t } = useLang();
  const { openDrawer } = useLeviDrawer();

  const facts = deriveLeviFacts({
    context: "full_project_intake",
    project: _formToProjectSlice(form || {}),
    intakeDraftProjectExists: Boolean(draftProjectId),
  });

  const empty = facts.customer_name_present === false && !draftProjectId;
  if (empty) {
    return (
      <div
        data-testid="levi-intake-coach-empty"
        className="rounded-lg border border-dashed border-[var(--jc-border)] p-3 bg-[var(--jc-surface)] text-xs text-[var(--jc-text-soft)]"
      >
        {t("levi_intake_coach.empty_hint")}
      </div>
    );
  }

  const sentence = composeIntakeStatusSentence(facts, t);
  const also = _extraObservations(facts, t);

  return (
    <section
      data-testid="levi-intake-coach"
      className="rounded-lg border border-[var(--jc-border)] p-4 bg-[var(--jc-surface)]"
    >
      <div className="flex items-start gap-3">
        <img
          src="/levi/levi-badge.webp"
          alt="Levi"
          data-testid="levi-intake-coach-avatar"
          className="w-10 h-10 shrink-0 rounded-full bg-white p-0.5 border border-[var(--jc-border)] object-cover"
          onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
        />
        <div className="flex-1 min-w-0">
          <div className="overline">{t("levi_intake_coach.header_kicker")}</div>
          <div
            data-testid="levi-intake-status"
            className="text-sm text-[var(--jc-text)] leading-snug"
          >
            {sentence}
          </div>
          {also.length > 0 && (
            <ul
              data-testid="levi-intake-also"
              className="mt-2 space-y-1 text-xs text-[var(--jc-text-soft)] list-disc pl-4"
            >
              {also.map((o) => (
                <li key={o.id} data-testid={`levi-intake-also-${o.id}`}>{o.text}</li>
              ))}
            </ul>
          )}
          <div className="mt-3">
            <button
              type="button"
              onClick={openDrawer}
              data-testid="levi-intake-ask-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md jc-btn-secondary"
            >
              <MessageCircleQuestion size={12} />
              {t("levi_intake_coach.ask_btn")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
